import { inject } from '@angular/core'
import { CanActivateFn, Router } from '@angular/router'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'

import { StorageService } from '../services/utils/storage.service'
import { SSO_ATTEMPT_KEY, SsoAttempt } from '../shared/utils/sso-attempt.util'

/**
 * Lets the portal's outcome through only when it answers the attempt in
 * progress: the `state` it carries has to be the one drawn for that attempt.
 *
 * Anything else is dropped where it came in - no navigation, no sign-out: a
 * late outcome of an attempt the user gave up, or a link someone else forged
 * (another Android app can claim the `com.ecovelo.<app>://` scheme). The in-app
 * browser is closed all the same, and the user stays where they were.
 *
 * An outcome with no `state` at all comes from a portal or a build older than
 * the PKCE mode. It cannot be told apart, so it is let through during the
 * transition.
 */
export const ssoAttemptGuard: CanActivateFn = async (route) => {
  // Injected before anything is awaited: the injection context does not
  // survive an `await`.
  const storage = inject(StorageService)
  const router = inject(Router)
  const state =
    route.queryParamMap.get('state') ??
    new URLSearchParams(route.fragment ?? '').get('state')
  if (!state) return true

  const attempt: SsoAttempt | null = await storage.get(SSO_ATTEMPT_KEY)
  if (attempt?.state === state) return true

  if (Capacitor.isNativePlatform()) {
    await Browser.close().catch(() => undefined)
  }

  // On a first load (the web lands here straight from the portal) there is
  // no screen to stay on.
  return router.navigated ? false : router.parseUrl('/')
}
